import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import MultiSelectModal from '../../Modal/MultiSelectModal';
import '@fortawesome/fontawesome-free/css/all.min.css';

export type EntityItem = { id: number | string; name?: string | null };

export type EntityListProps<T extends EntityItem> = {
  items: T[];
  getDisplayName: (item: T) => string;
  onUpdate?: (item: T) => void;
  onDelete?: (item: T) => void;
  hideActions?: boolean;
};

function EntityList<T extends EntityItem>({
  items,
  getDisplayName,
  onUpdate,
  onDelete,
  hideActions = false,
}: EntityListProps<T>) {
  const [menuOpenItem, setMenuOpenItem] = useState<T | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<DOMRect | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);

  const closeMenu = useCallback(() => {
    setMenuOpenItem(null);
    setMenuAnchor(null);
  }, []);

  const toggleMenu = (item: T, button: HTMLButtonElement) => {
    if (menuOpenItem?.id === item.id) {
      closeMenu();
      return;
    }
    setMenuOpenItem(item);
    setMenuAnchor(button.getBoundingClientRect());
  };

  useEffect(() => {
    if (!menuOpenItem) return;

    const updateAnchor = () => {
      const btn = document.getElementById(`entity-menu-btn-${menuOpenItem.id}`);
      if (btn) setMenuAnchor(btn.getBoundingClientRect());
    };

    updateAnchor();
    window.addEventListener('resize', updateAnchor);
    window.addEventListener('scroll', updateAnchor, true);

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const menuId = `entity-menu-${menuOpenItem.id}`;
      const btnId = `entity-menu-btn-${menuOpenItem.id}`;
      if (
        document.getElementById(menuId)?.contains(target) ||
        document.getElementById(btnId)?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', updateAnchor);
      window.removeEventListener('scroll', updateAnchor, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpenItem, closeMenu]);

  const handleDeleteClick = (item: T) => {
    closeMenu();
    setDeleteTarget(item);
  };

  const handleConfirmDelete = () => {
    if (deleteTarget && onDelete) {
      onDelete(deleteTarget);
      setDeleteTarget(null);
    }
  };

  const handleUpdateClick = (item: T) => {
    closeMenu();
    onUpdate?.(item);
  };

  const menuPortal =
    menuOpenItem && menuAnchor && !hideActions && onUpdate && onDelete
      ? createPortal(
          <ul
            id={`entity-menu-${menuOpenItem.id}`}
            className="dropdown-menu dropdown-menu-end show sb-entity-action-menu"
            style={{
              top: menuAnchor.bottom,
              left: menuAnchor.right,
              transform: 'translateX(-100%)',
            }}
            role="menu"
          >
            <li>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => handleUpdateClick(menuOpenItem)}
              >
                Güncelle
              </button>
            </li>
            <li>
              <button
                type="button"
                className="dropdown-item text-danger"
                onClick={() => handleDeleteClick(menuOpenItem)}
              >
                Sil
              </button>
            </li>
          </ul>,
          document.body,
        )
      : null;

  return (
    <>
      <ul className="list-group list-group-flush">
        {items.map((item) => (
          <li
            key={String(item.id)}
            className="list-group-item d-flex justify-content-between align-items-center"
          >
            <span>{getDisplayName(item)}</span>
            {!hideActions && onUpdate && onDelete && (
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-secondary p-0"
                  id={`entity-menu-btn-${item.id}`}
                  aria-expanded={menuOpenItem?.id === item.id}
                  aria-haspopup="menu"
                  onClick={(e) => toggleMenu(item, e.currentTarget)}
                >
                  <i className="fas fa-ellipsis-v" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {menuPortal}
      {deleteTarget && (
        <MultiSelectModal
          title="Silmek istediğinize emin misiniz?"
          message={getDisplayName(deleteTarget)}
          onAccept={handleConfirmDelete}
          onReject={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}

export default EntityList;
